import { translate } from '@box-core/services';
import { Review } from '@box-types';
import React, { JSX, useEffect, useState } from 'react';
import ReviewComponent from '../review/review.component';

type ReviewsListProps = {
  reviews: Review[];
  showReviews?: boolean;
  emptyState?: {
    primaryText?: string;
    secondaryText?: string;
  };
};

const StarClusterIcon: JSX.Element = (
  <svg xmlns="http://www.w3.org/2000/svg" width="104" height="40">
    <path
      fill="#eee"
      d="m102.253 18.783-8.71-.866c-.55-.053-1.024-.42-1.248-.972l-3.12-7.96a1.467 1.467 0 0 0-2.77 0l-3.095 7.96c-.2.551-.699.92-1.248.972l-8.71.866c-1.298.132-1.823 1.84-.85 2.759l6.565 6.068c.424.394.599.972.474 1.55l-1.971 8.51c-.3 1.34 1.074 2.444 2.222 1.735l7.263-4.493a1.445 1.445 0 0 1 1.522 0l7.263 4.493c1.148.709 2.52-.368 2.221-1.734l-1.947-8.511a1.621 1.621 0 0 1 .475-1.55l6.564-6.068c.948-.92.399-2.627-.899-2.759ZM66.253 10.783l-8.71-.866c-.55-.053-1.024-.42-1.248-.972l-3.12-7.96a1.467 1.467 0 0 0-2.77 0l-3.095 7.96c-.2.551-.699.92-1.248.972l-8.71.866c-1.298.132-1.823 1.84-.85 2.759l6.565 6.068c.424.394.599.972.474 1.55l-1.971 8.51c-.3 1.34 1.074 2.444 2.222 1.735l7.263-4.493a1.445 1.445 0 0 1 1.522 0l7.263 4.493c1.148.709 2.52-.368 2.221-1.734l-1.947-8.511a1.621 1.621 0 0 1 .475-1.55l6.564-6.068c.948-.92.399-2.627-.899-2.759ZM30.253 18.783l-8.71-.866c-.55-.053-1.024-.42-1.248-.972l-3.12-7.96a1.467 1.467 0 0 0-2.77 0l-3.095 7.96c-.2.551-.699.92-1.248.972l-8.71.866c-1.298.132-1.823 1.84-.85 2.759l6.565 6.068c.424.394.599.972.474 1.55L5.57 37.67c-.3 1.34 1.074 2.444 2.222 1.735l7.263-4.493a1.445 1.445 0 0 1 1.522 0l7.263 4.493c1.148.709 2.52-.368 2.221-1.734l-1.947-8.511a1.621 1.621 0 0 1 .475-1.55l6.564-6.068c.948-.92.399-2.627-.899-2.759Z"
    />
  </svg>
);

const ReviewsList: React.FC<ReviewsListProps> = ({ reviews, showReviews = true, emptyState }) => {
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    // Show skeleton for at least 300ms on mount
    const timer = setTimeout(() => {
      setShowSkeleton(false);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  const hasReviews = Array.isArray(reviews) && reviews.length > 0;

  if ((!hasReviews && emptyState) || !showReviews) {
    return (
      <div className="reviews-list-component">
        <div className="reviews-list-component-empty-state">
          <p className="reviews-list-component-text">{translate(emptyState.primaryText)}</p>
          {StarClusterIcon}
          {emptyState.secondaryText && (
            <p className="reviews-list-component-text">{translate(emptyState.secondaryText)}</p>
          )}
        </div>
      </div>
    );
  }

  if (showSkeleton) {
    return (
      <div className="reviews-list-component">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="reviews-list-component-skeleton-review"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="reviews-list-component">
      {reviews.map((review, index) => (
        <React.Fragment key={index}>
          <ReviewComponent review={review} />
          {index < reviews.length - 1 && <div className="box-divider  reviews-list-component-divider" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ReviewsList;
